import {
  type HhDataItem,
  usePreviewChangelog,
  useSiteUpdate,
} from "@inrange/building-manager-api-client";
import {
  getESysName,
  SiteChangeLog,
} from "@inrange/building-manager-api-client/models-site";
import { ReactNode, useCallback, useMemo, useState } from "react";
import { Alert, Button, Form, Modal, Table } from "react-bootstrap";
import { useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { ChangelogModal } from "../site/siteSections/ChangelogModal";
import { SiteDemandRow } from "./DemandDataSiteAssignment";

const DemandDataSiteRemoval = ({
  hhDataItem,
  onClose,
}: {
  hhDataItem: HhDataItem;
  onClose: () => void;
}) => {
  const siteUpdate = useSiteUpdate();
  const previewChangelog = usePreviewChangelog();
  const queryClient = useQueryClient();

  const [selectedRowIds, setSelectedRowIds] = useState<Set<string>>(new Set());

  // Changelog preview state
  const [showChangelogModal, setShowChangelogModal] = useState(false);
  const [changelogPreview, setChangelogPreview] =
    useState<SiteChangeLog | null>(null);
  const [changelogSiteId, setChangelogSiteId] = useState<string | null>(null);

  const [alertStyle, setAlertStyle] = useState<string>("light");
  const [alertMessage, setAlertMessage] = useState<
    ReactNode | string | undefined
  >(undefined);

  // Transform sites into flat (Site, Demand) rows - only showing sites currently assigned to this demand file
  const assignedRows: SiteDemandRow[] = useMemo(() => {
    const rows: SiteDemandRow[] = [];

    // Get the set of site/demand mappings for this demand file
    const assignedMappings = hhDataItem.siteMappings;

    for (const mapping of assignedMappings) {
      const site = mapping.site;
      const esysCount = Object.keys(site.esys).length;
      const demandCount = Object.keys(site.demand).length;
      const demandIdx = String(mapping.demandIdx);
      const demand = site.demand[demandIdx];
      if (!demand) continue;

      // Find the esys that maps to this demandIdx
      for (const [esysIdx, esys] of Object.entries(site.esys)) {
        if (String(esys.demandIdx) === demandIdx) {
          const esysName = getESysName(esysCount, esys);
          const displayLabel =
            demandCount === 1
              ? site.name
              : `${site.name} -> ${esysName.trim() || `Demand ${parseInt(demandIdx) + 1}`}`;

          rows.push({
            siteId: site.id,
            siteName: site.name,
            demandIdx,
            esysIdx,
            esys,
            demand,
            displayLabel,
            orgs: site.siteOwnerships.map((o) => ({
              orgID: o.orgID,
              name: o.name,
            })),
          });
        }
      }
    }

    return rows.sort((a, b) =>
      a.displayLabel.toLowerCase().localeCompare(b.displayLabel.toLowerCase())
    );
  }, [hhDataItem.siteMappings]);

  const onSetSelected = useCallback((rowId: string, selected: boolean) => {
    setSelectedRowIds((prev) => {
      const newSet = new Set(prev);
      if (selected) {
        newSet.add(rowId);
      } else {
        newSet.delete(rowId);
      }
      return newSet;
    });
  }, []);

  const getRowId = (row: SiteDemandRow) =>
    `${row.siteId}:${row.demandIdx}:${row.esysIdx}`;

  const handlePreviewChangelog = async (row: SiteDemandRow) => {
    // Build payload to preview removing the demand file
    const demandPayload: {
      [key: string]: {
        hhDemandGuid?: string | null;
        hhDemandFileDescription?: string;
      };
    } = {};
    demandPayload[row.demandIdx] = {
      hhDemandGuid: null,
      hhDemandFileDescription: "",
    };
    const update = {
      site: {
        id: row.siteId,
        demand: demandPayload,
      },
    };

    try {
      const response = await previewChangelog.mutateAsync({
        siteId: row.siteId,
        update,
      });
      if (response.log) {
        setChangelogPreview(response.log);
      } else {
        setChangelogPreview({
          siteID: row.siteId,
          time: Math.floor(Date.now() / 1000),
          sortTime: Math.floor(Date.now() / 1000),
          updatedBy: "",
          userChange: {},
          recalculated: {},
        });
      }
      setChangelogSiteId(row.siteId);
      setShowChangelogModal(true);
    } catch (error: any) {
      console.error("Error previewing changelog:", error);
      setAlertStyle("danger");
      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        "Failed to preview changelog";
      setAlertMessage(errorMessage);
    }
  };

  const handleRemoveDemand = async () => {
    const selectedRows = assignedRows.filter((row) =>
      selectedRowIds.has(getRowId(row))
    );

    if (selectedRows.length === 0) return;

    setAlertStyle("light");
    setAlertMessage(`Removing demand from ${selectedRows.length} site(s)...`);

    // Group updates by siteId since we may have multiple demands per site
    const updatesBySite: {
      [siteId: string]: { demandUpdates: { demandIdx: string }[] };
    } = {};

    for (const row of selectedRows) {
      if (!updatesBySite[row.siteId]) {
        updatesBySite[row.siteId] = { demandUpdates: [] };
      }
      updatesBySite[row.siteId].demandUpdates.push({
        demandIdx: row.demandIdx,
      });
    }

    const mutations = Object.entries(updatesBySite).map(
      ([siteId, { demandUpdates }]) => {
        // Build the demand update payload to remove the demand file association
        const demandPayload: {
          [key: string]: {
            hhDemandGuid?: string | null;
            hhDemandFileDescription?: string;
          };
        } = {};
        for (const { demandIdx } of demandUpdates) {
          demandPayload[demandIdx] = {
            hhDemandGuid: null,
            hhDemandFileDescription: "",
          };
        }

        return siteUpdate.mutateAsync({
          siteId,
          payload: {
            site: {
              id: siteId,
              demand: demandPayload,
            },
          },
        });
      }
    );

    try {
      const results = await Promise.allSettled(mutations);
      queryClient.invalidateQueries(["site"]);
      queryClient.invalidateQueries(["sites"]);
      queryClient.invalidateQueries(["hh-data"]);

      for (const siteId of Object.keys(updatesBySite)) {
        queryClient.invalidateQueries(["site", siteId]);
      }

      const successfulUpdates = results.filter(
        (result) => result.status === "fulfilled"
      ).length;
      const failedUpdates = results.filter(
        (result) => result.status === "rejected"
      ).length;

      const failedSiteDetails = results
        .filter((result) => result.status === "rejected")
        .map((result, idx) => {
          const siteId = Object.keys(updatesBySite)[idx];
          const errorMessage =
            (result as PromiseRejectedResult).reason?.response?.data?.message ||
            (result as PromiseRejectedResult).reason?.message ||
            (result as PromiseRejectedResult).reason?.toString() ||
            "Unknown error";
          const siteName =
            assignedRows.find((r) => r.siteId === siteId)?.siteName || siteId;
          return { siteId, siteName, errorMessage };
        });

      if (failedUpdates === 0) {
        setAlertStyle("success");
        setAlertMessage(
          `Successfully removed demand data from ${successfulUpdates} site(s)`
        );
        setSelectedRowIds(new Set());
      } else {
        setAlertStyle("danger");
        setAlertMessage(
          <>
            <div>
              Failed to update {failedUpdates} out of{" "}
              {Object.keys(updatesBySite).length} site(s)
            </div>
            <ul style={{ margin: 0 }}>
              {failedSiteDetails
                .slice(0, 10)
                .map(({ siteId, siteName, errorMessage }) => (
                  <li key={siteId}>
                    <Link to={`/site/${siteId}/edit`}>{siteName}</Link> -{" "}
                    {errorMessage}
                  </li>
                ))}
              {failedSiteDetails.length > 10 && (
                <li>{`and ${failedSiteDetails.length - 10} more...`}</li>
              )}
            </ul>
          </>
        );
      }
    } catch (error) {
      setAlertStyle("danger");
      setAlertMessage(`An unexpected error occurred while updating sites`);
    }
  };

  return (
    <Modal show={true} onHide={onClose} size="xl" style={{ marginTop: "5vh" }}>
      <Modal.Header closeButton>
        <Modal.Title>
          Remove Demand Data from Sites
          <div
            style={{ fontSize: "14px", fontWeight: "normal", color: "#666" }}
          >
            File: {hhDataItem.fileName || hhDataItem.guid}
          </div>
        </Modal.Title>
      </Modal.Header>
      <Modal.Body style={{ maxHeight: "80vh", overflow: "auto" }}>
        {alertMessage && (
          <Alert
            variant={alertStyle}
            onClose={() => setAlertMessage(undefined)}
            dismissible={alertStyle !== "light"}
          >
            <div>{alertMessage}</div>
          </Alert>
        )}

        {assignedRows.length === 0 ? (
          <Alert variant="info">
            This demand file is not currently assigned to any sites.
          </Alert>
        ) : (
          <>
            <div
              style={{
                display: "flex",
                gap: "16px",
                marginBottom: "16px",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <div style={{ fontSize: "14px" }}>
                {assignedRows.length} site(s) currently assigned
                {selectedRowIds.size > 0 &&
                  ` (${selectedRowIds.size} selected)`}
              </div>
              <Button
                variant="danger"
                onClick={handleRemoveDemand}
                disabled={selectedRowIds.size === 0}
              >
                Remove demand from {selectedRowIds.size} site(s)
              </Button>
            </div>

            <Table striped bordered size="sm" responsive>
              <thead>
                <tr>
                  <th style={{ width: "40px" }}>
                    <Form.Check
                      type="checkbox"
                      checked={
                        assignedRows.length > 0 &&
                        selectedRowIds.size === assignedRows.length
                      }
                      onChange={() => {
                        if (selectedRowIds.size !== assignedRows.length) {
                          setSelectedRowIds(
                            new Set(assignedRows.map((row) => getRowId(row)))
                          );
                        } else {
                          setSelectedRowIds(new Set());
                        }
                      }}
                    />
                  </th>
                  <th>Site / Demand</th>
                  <th>Organisations</th>
                  <th style={{ width: "140px" }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {assignedRows.map((row) => {
                  const rowId = getRowId(row);
                  const isSelected = selectedRowIds.has(rowId);
                  return (
                    <tr key={rowId}>
                      <td>
                        <Form.Check
                          type="checkbox"
                          checked={isSelected}
                          onChange={(e) =>
                            onSetSelected(rowId, e.target.checked)
                          }
                        />
                      </td>
                      <td>
                        <Link to={`/site/${row.siteId}/edit`}>
                          {row.displayLabel}
                        </Link>
                      </td>
                      <td>
                        {row.orgs
                          .filter((o) => o.name)
                          .map((o) => o.name)
                          .join(", ") || "—"}
                      </td>
                      <td>
                        <Button
                          variant="outline-secondary"
                          size="sm"
                          onClick={() => handlePreviewChangelog(row)}
                          disabled={previewChangelog.isLoading}
                        >
                          Preview changelog
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onClose}>
          Close
        </Button>
      </Modal.Footer>
      {showChangelogModal && changelogPreview && changelogSiteId && (
        <ChangelogModal
          logItem={changelogPreview}
          siteId={changelogSiteId}
          lossFactorsByKey={{}}
          onClose={() => setShowChangelogModal(false)}
          zIndex={1060}
        />
      )}
    </Modal>
  );
};

export default DemandDataSiteRemoval;
